import React from 'react';
import { Navigation, ExternalLink } from 'lucide-react';

interface GetDirectionsProps {
  address: {
    street: string;
    city: string;
    state: string;
    zip?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  fairName?: string;
  className?: string;
}

export default function GetDirections({ address, fairName, className = '' }: GetDirectionsProps) {
  const handleGetDirections = () => {
    const { coordinates, street, city, state, zip } = address;

    // Use coordinates if available (more accurate)
    if (coordinates?.lat != null && coordinates?.lng != null) {
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Fallback to address string
      const addressString = `${street}, ${city}, ${state}${zip ? ' ' + zip : ''}`;
      const encodedAddress = encodeURIComponent(addressString);
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleViewOnMap = () => {
    const { coordinates, street, city, state, zip } = address;

    if (coordinates?.lat != null && coordinates?.lng != null) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    } else {
      const addressString = `${street}, ${city}, ${state}${zip ? ' ' + zip : ''}`;
      const encodedAddress = encodeURIComponent(addressString);
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Navigation className="h-5 w-5 text-secondary" />
        <span>Location & Directions</span>
      </h3>

      <div className="space-y-3">
        {/* Address Display */}
        <div className="text-sm text-gray-700">
          <div className="font-medium">{fairName}</div>
          <div>{address.street}</div>
          <div>
            {address.city}, {address.state} {address.zip}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleGetDirections}
            className="flex-1 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors font-medium flex items-center justify-center gap-2"
          >
            <Navigation className="h-4 w-4" />
            <span>Get Directions</span>
          </button>
          <button
            onClick={handleViewOnMap}
            className="flex-1 bg-white text-secondary border border-primary px-4 py-2 rounded-lg hover:bg-secondary-light/50 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            <span>View on Map</span>
          </button>
        </div>

        {/* Additional Info */}
        <p className="text-xs text-gray-500 text-center">
          Opens in Google Maps in a new tab
        </p>
      </div>
    </div>
  );
}
